import axios from 'axios';
import { enqueueRequest, bootDurableQueue } from './durableQueue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Boot the durable replay loop once
try { bootDurableQueue(); } catch { /* SSR or storage disabled */ }

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Endpoints we never queue (auth + read-only paths shouldn't be queued)
const NON_QUEUEABLE = [
  '/auth/login',
  '/auth/signup',
  '/auth/me',
  '/admin/error-logs/client',
  '/events/stream',
];
function isQueueableWrite(config) {
  const method = (config?.method || '').toUpperCase();
  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) return false;
  const url = config?.url || '';
  if (NON_QUEUEABLE.some((p) => url.includes(p))) return false;
  return true;
}

// Handle auth errors + auto-report 5xx + durable-queue on network failure
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // FastAPI 422 returns detail as a list of {type, loc, msg, input}. Rendering that
    // object in a toast crashes React (#31) — flatten to a readable string.
    const detail = error.response?.data?.detail;
    if (detail && typeof detail !== 'string') {
      const msgs = Array.isArray(detail)
        ? detail.map((d) => (d && typeof d === 'object' ? `${(d.loc || []).slice(-1)[0] || 'field'}: ${d.msg || 'invalid'}` : String(d)))
        : [typeof detail === 'object' ? (detail.msg || detail.message || JSON.stringify(detail)) : String(detail)];
      error.response.data.detail = msgs.join('; ');
    }
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // ---- Durability: if a write fails with NO response (network drop, offline, mid-request kill),
    // queue it locally so the user never loses their action.
    const noResponse = !error.response;
    if (noResponse && isQueueableWrite(error.config)) {
      try {
        const userId = (() => {
          try {
            const t = localStorage.getItem('token');
            if (!t) return null;
            // jwt: header.payload.signature
            const payload = JSON.parse(atob(t.split('.')[1]));
            return payload.user_id || payload.sub || null;
          } catch { return null; }
        })();
        const queued = await enqueueRequest(error.config, userId);
        // Return a synthetic 202 response so the calling code does NOT throw.
        return {
          data: { queued: true, queueId: queued.id, message: 'Saved locally — will sync when back online.' },
          status: 202,
          statusText: 'Queued',
          headers: {},
          config: error.config,
        };
      } catch {
        // fall through to original reject
      }
    }

    // Report 5xx and network errors (but not client 4xx)
    const status = error.response?.status;
    if ((status >= 500 && status < 600) || !error.response) {
      try {
        const token = localStorage.getItem('token');
        const url = (error.config?.baseURL || '') + (error.config?.url || '');
        axios.post(
          `${BACKEND_URL}/api/admin/error-logs/client`,
          {
            message: `${error.config?.method?.toUpperCase() || 'REQ'} ${error.config?.url || '?'} — ${status || 'network'} — ${error.message || ''}`,
            stack_trace: `${error.stack || ''}\nURL: ${url}\nResponse: ${JSON.stringify(error.response?.data || {}).slice(0, 500)}`,
            route: window.location.pathname,
            user_agent: navigator.userAgent,
          },
          token ? { headers: { Authorization: `Bearer ${token}` } } : {}
        ).catch(() => {}); // don't loop
      } catch {
        // ignore
      }
    }
    return Promise.reject(error);
  }
);

export default api;
